import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useMobileContext } from '../components/Layout';
import { MobileHeader } from '../components/mobile';
import { Receipt, Download, Eye, CreditCard, CheckCircle, Clock, XCircle, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Factures = () => {
  const { token } = useAuth();
  const { isMobile, onMenuClick } = useMobileContext();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => { 
    fetchInvoices(); 
  }, [token]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/factures`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency }).format(amount);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-500" />;
      case 'cancelled':
      case 'failed':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    const colors = { 
      paid: 'bg-green-100 text-green-700', 
      pending: 'bg-yellow-100 text-yellow-700', 
      cancelled: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    const labels = { 
      paid: 'Payée', 
      pending: 'En attente', 
      cancelled: 'Annulée',
      failed: 'Échouée'
    };
    return labels[status] || status;
  };

  const totalPaid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.amount || 0), 0);
  const totalPending = invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + (inv.amount || 0), 0);

  const downloadInvoicePDF = (invoice) => {
    // Generate a simple invoice text (in production, this would generate a real PDF)
    const invoiceContent = `
FACTURE ${invoice.id}
========================

Date: ${formatDate(invoice.created_at)}
Module: ${invoice.module_name}

Montant HT: ${formatCurrency(invoice.amount / 1.2)}
TVA (20%): ${formatCurrency(invoice.amount - invoice.amount / 1.2)}
------------------------
Total TTC: ${formatCurrency(invoice.amount)}

Statut: ${getStatusLabel(invoice.status)}

SyndicAppli - Gestion de copropriété
    `.trim();

    const blob = new Blob([invoiceContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facture-${invoice.id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <>
        {isMobile && <MobileHeader title="Mes Factures" onMenuClick={onMenuClick} />}
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00D3A8]"></div>
        </div>
      </>
    );
  }

  return (
    <>
      {isMobile && (
        <MobileHeader 
          title={selectedInvoice ? `Facture #${selectedInvoice.id?.slice(0,8)}` : "Mes Factures"}
          onMenuClick={onMenuClick}
          showBackButton={!!selectedInvoice}
          rightAction={selectedInvoice ? (
            <button onClick={() => setSelectedInvoice(null)} className="p-2 -mr-2 text-white hover:bg-white/10 rounded-full">
              <X size={24} />
            </button>
          ) : undefined}
        />
      )}
    <div className={`space-y-4 md:space-y-6 ${isMobile ? 'p-4' : ''}`} data-testid="factures-page">
      {!isMobile && <h1 className="text-2xl font-bold text-gray-800">Mes Factures</h1>}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4" data-testid="total-invoices-card">
          <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
            <Receipt size={16} />
            <span>Total factures</span>
          </div>
          <div className="text-2xl font-bold text-gray-800">{invoices.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4" data-testid="total-amount-card">
          <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
            <CreditCard size={16} />
            <span>Montant total</span>
          </div>
          <div className="text-2xl font-bold text-[#00D3A8]">
            {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.amount || 0), 0))}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4" data-testid="paid-amount-card">
          <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
            <CheckCircle size={16} className="text-green-500" />
            <span>Total payé</span>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(totalPaid)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4" data-testid="pending-amount-card">
          <div className="flex items-center space-x-2 text-gray-500 text-sm mb-1">
            <Clock size={16} className="text-yellow-500" />
            <span>En attente</span>
          </div>
          <div className="text-2xl font-bold text-yellow-600">
            {formatCurrency(totalPending)}
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden" data-testid="invoices-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Facture</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50" data-testid={`invoice-row-${invoice.id}`}>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-medium text-gray-800">{invoice.id}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">{formatDate(invoice.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-800 font-medium">{invoice.module_name || 'Module'}</div>
                    {invoice.stripe_session_id && (
                      <div className="text-xs text-gray-400">Réf: {invoice.stripe_session_id.slice(-8)}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {formatCurrency(invoice.amount, invoice.currency)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${getStatusColor(invoice.status)}`}>
                      {getStatusIcon(invoice.status)}
                      <span>{getStatusLabel(invoice.status)}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => setSelectedInvoice(invoice)} 
                        className="text-gray-400 hover:text-[#00D3A8] transition-colors" 
                        title="Voir les détails"
                        data-testid={`view-invoice-${invoice.id}`}
                      >
                        <Eye size={18} />
                      </button>
                      <button 
                        onClick={() => downloadInvoicePDF(invoice)}
                        className="text-gray-400 hover:text-[#00D3A8] transition-colors" 
                        title="Télécharger"
                        data-testid={`download-invoice-${invoice.id}`}
                      >
                        <Download size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {invoices.length === 0 && (
          <div className="text-center py-12 text-gray-500" data-testid="no-invoices">
            <Receipt size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Aucune facture</p>
            <p className="text-sm mt-1">Vos factures apparaîtront ici après votre premier achat.</p>
          </div>
        )}
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="invoice-modal">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">Facture</h2>
                <p className="text-sm text-gray-500 font-mono">{selectedInvoice.id}</p>
              </div>
              <button 
                onClick={() => setSelectedInvoice(null)} 
                className="text-gray-400 hover:text-gray-600 p-1"
                data-testid="close-modal-btn"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Date</span>
                <span className="font-medium text-gray-800">{formatDate(selectedInvoice.created_at)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Description</span>
                <span className="font-medium text-gray-800">{selectedInvoice.module_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Montant HT</span>
                <span className="font-medium text-gray-800">{formatCurrency(selectedInvoice.amount / 1.2)}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">TVA (20%)</span>
                <span className="font-medium text-gray-800">{formatCurrency(selectedInvoice.amount - selectedInvoice.amount / 1.2)}</span>
              </div>
              <div className="flex justify-between py-3 bg-gray-50 -mx-2 px-2 rounded-lg">
                <span className="font-semibold text-gray-800">Total TTC</span>
                <span className="font-bold text-xl text-[#00D3A8]">{formatCurrency(selectedInvoice.amount)}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-500">Statut</span>
                <span className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${getStatusColor(selectedInvoice.status)}`}>
                  {getStatusIcon(selectedInvoice.status)}
                  <span>{getStatusLabel(selectedInvoice.status)}</span>
                </span>
              </div>
              {selectedInvoice.stripe_session_id && (
                <div className="flex justify-between py-2 border-t border-gray-100">
                  <span className="text-gray-500">Référence Stripe</span>
                  <span className="font-mono text-xs text-gray-600">{selectedInvoice.stripe_session_id.slice(-12)}</span>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button 
                onClick={() => setSelectedInvoice(null)} 
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Fermer
              </button>
              <button 
                onClick={() => downloadInvoicePDF(selectedInvoice)}
                className="px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] flex items-center space-x-2 transition-colors"
                data-testid="download-pdf-btn"
              >
                <Download size={18} />
                <span>Télécharger</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Factures;
